import React from 'react';

import CountryInfo from '../CountryInfo';

const STIMULUS_LOAN_PACKAGE = {
    title: 'Coronavirus Business Interruption Loan Scheme (CBILS)',
    content: (
        <div>
            <p className="eds-text-bl eds-l-mar-bot-4">Key features</p>
            <ul>
                <li>
                    <strong>Up to £5m facility:</strong>&nbsp; The maximum value
                    of a facility provided under the scheme will be £5m,
                    available on repayment terms of up to six years.
                </li>
                <li>
                    <strong>80% guarantee:</strong>&nbsp; The scheme provides
                    the lender with a government-backed, partial guarantee (80%)
                    against the outstanding facility balance, subject to an
                    overall cap per lender.
                </li>
                <li>
                    <strong>
                        No guarantee fee for SMEs to access the scheme:
                    </strong>
                    &nbsp; No fee for smaller businesses. Lenders will pay a fee
                    to access the scheme.
                </li>
                <li>
                    <strong>
                        Interest and fees paid by Government for 12 months:
                    </strong>
                    &nbsp; The Government will make a Business Interruption
                    Payment to cover the first 12 months of interest payments
                    and any lender-levied fees, so smaller businesses will
                    benefit from no upfront costs and lower initial repayments.
                </li>
                <li>
                    <strong>Finance terms:</strong>&nbsp; Finance terms are up
                    to six years for term loans and asset finance facilities.
                    For overdrafts and invoice finance facilities, terms will be
                    up to three years.
                </li>
                <li>
                    <strong>Security:</strong>&nbsp; At the discretion of the
                    lender, the scheme may be used for unsecured lending for
                    facilities of £250,000 and under. For facilities above
                    £250,000, the lender must establish a lack or absence of
                    security prior to businesses using CBILS. If the lender can
                    offer finance on normal commercial terms without the need to
                    make use of the scheme, they will do so.
                </li>
                <li>
                    <strong>
                        The borrower always remains 100% liable for the debt.
                    </strong>
                </li>
            </ul>

            <p className="eds-text-bl eds-l-mar-bot-4 eds-l-mar-top-8">
                How to access the scheme
            </p>
            <p>
                CBILS is available through the British Business Bank’s 40+
                accredited lenders, which are listed on the&nbsp;
                <a
                    href="https://www.british-business-bank.co.uk/ourpartners/coronavirus-business-interruption-loan-scheme-cbils/accredited-lenders/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="eds-text-weight--heavy"
                >
                    British Business Bank website
                </a>
                .
            </p>
            <br />
            <p>
                In the first instance, businesses should approach their own
                provider – ideally via the lender’s website. They may also
                consider approaching other lenders if they are unable to access
                the finance they need.
            </p>
            <br />
            <p>
                Decision-making on whether you are eligible for CBILS is fully
                delegated to the 40+ accredited CBILS lenders. These lenders
                range from high-street banks, to challenger banks, asset-based
                lenders and smaller specialist local lenders.
            </p>

            <p className="eds-text-bl eds-l-mar-bot-4 eds-l-mar-top-8">
                Types of financing available
            </p>
            <p>
                CBILS supports a wide range of business finance facilities,
                including:
            </p>
            <ul>
                <li>Term loans</li>
                <li>Overdrafts</li>
                <li>Asset finance</li>
                <li>Invoice finance</li>
            </ul>
            <p>
                Note: Not every lender can provide every type of finance listed.
            </p>

            <p className="eds-text-bl eds-l-mar-bot-4 eds-l-mar-top-8">
                Eligibility criteria
            </p>
            <p>
                The scheme is designed to support smaller businesses (SMEs) who
                don’t meet a lender’s normal lending requirements for a fully
                commercial loan or other facility, but who are considered viable
                in the longer-term.
            </p>
            <br />
            <p>
                To be eligible for a facility under CIBLS, your business must:
            </p>
            <ul>
                <li>
                    Be UK based in its business activity with annual turnover or
                    no more than £45m
                </li>
                <li>
                    Have a borrowing proposal which, were it not for the
                    COVID-19 pandemic, would be considered viable by the lender,
                    and for which the lender believes the provision of finance
                    will enable your business to trade out of any
                    short-to-medium term difficulty
                </li>
                <li>
                    Smaller businesses from any sector can apply for the full
                    amount of the facility.
                </li>
            </ul>
        </div>
    ),
    links: [
        {
            title: 'The Coronavirus Business Interruption Loan Scheme (CBILS) is now available through participating lenders',
            url: 'https://www.british-business-bank.co.uk/ourpartners/coronavirus-business-interruption-loan-scheme-cbils/for-businesses-and-advisors/',
        },
    ],
};

const GUIDANCE_FOR_BUSINESSES = {
    title: 'Guidance for employees, employers and businesses',
    content: (
        <div>
            <p className="eds-text-bl eds-l-mar-bot-4">
                Information on healthcare advice for employers and support for
                businesses
            </p>
            <ul>
                <li>
                    £3000 cash grant (no repayment liability) made available for
                    700,000 of the UK&apos;s smallest businesses. Local
                    authorities will be contacting its residents for the loan
                </li>
                <li>
                    Coronavirus Business Interruption Loan Scheme: The British
                    Business Bank will enable businesses with turnover less than
                    £41m to apply for a loan of up to £1.2m, government covering
                    up to 80% of any losses with no fees.
                </li>
                <li>
                    Sick pay coverage: For businesses with more than 250
                    employees, the cost of 14 days of statutory sick pay will be
                    refunded by the government.
                </li>
                <li>
                    COVID-19 Corporate Financing Facility: Bank of England will
                    buy short term debt from larger companies.
                </li>
                <li>
                    <strong>
                        Applications for the above will be available on Monday,
                        March 23rd.
                    </strong>
                </li>
            </ul>
        </div>
    ),
    links: [
        {
            title: 'Coronavirus (COVID-19) guidance for employees, employers and businesses',
            url: 'https://www.gov.uk/government/news/coronavirus-covid-19-guidance-for-employees-employers-and-businesses',
        },
        {
            title: 'COVID-19: support for businesses',
            url: 'https://www.gov.uk/government/publications/guidance-to-employers-and-businesses-about-covid-19/covid-19-support-for-businesses',
        },
    ],
};

const LLOYDS_BANK_RESPONSE = {
    title: "Lloyd's Banking Group Response",
    content: (
        <div>
            <p>
                Lloyds Banking Group has announced that it has allocated up to
                £2 billion of arrangement fee free finance assigned to
                specifically support Britain’s small and medium sized
                enterprises (SMEs) to ensure that they have access to additional
                finance to overcome any impact COVID-19 causes to their
                cashflow.
            </p>
            <p className="eds-text-bl eds-l-mar-bot-4 eds-l-mar-top-8">
                Details
            </p>
            <ul>
                <li>
                    No arrangement fees for new overdrafts or overdraft limit
                    increases
                </li>
                <li>
                    No arrangement fees for new or increased invoice discounting
                    and finance facilities
                </li>
                <li>
                    In certain circumstances, repayment holidays to be provided,
                    to those businesses impacted the most*
                </li>
            </ul>
        </div>
    ),
    links: [
        {
            title: 'Lloyds Banking Group ready to support UK small businesses impacted by COVID-19',
            url: 'https://www.lloydsbankinggroup.com/Media/Press-Releases/2020-press-releases/lloyds-banking-group/lloyds-banking-group-ready-to-support-uk-small-businesses-impacted-by-covid-19/',
        },
        {
            title: 'Support for Small Businesses',
            url: 'https://www.lloydsbank.com/business/coronavirus.html',
        },
    ],
};

const NATWEST_BANK_RESPONSE = {
    title: 'NatWest Bank Response',
    content: (
        <div>
            <p>
                £5bn of funding for SMEs. Includes temporary emergency loans
                with no fees.
            </p>
        </div>
    ),
    links: [
        {
            title: 'NatWest pledges £5bn Working Capital Support for SMEs during Coronavirus outbreak',
            url: 'https://www.rbs.com/rbs/news/2020/03/natwest-pledges-p5bn-working-capital-support-for-smes-during-cor.html',
        },
        {
            title: 'NatWest Business and coronavirus: Support Center (includes bank contact info)',
            url: 'https://www.business.natwest.com/business/support-centre/service-status/coronavirus.html',
        },
    ],
};

const COUNTRY_INFO = {
    countryName: 'United Kingdom',
    lastUpdated: 'March 23, 2020',
    introSection: (
        <p>
            The United Kingdom has approved the Coronavirus Business
            Interruption Loan Scheme (CBILS) that is now available through the
            British Business Bank’s 40+ accredited lenders.
        </p>
    ),
    sections: [
        STIMULUS_LOAN_PACKAGE,
        GUIDANCE_FOR_BUSINESSES,
        LLOYDS_BANK_RESPONSE,
        NATWEST_BANK_RESPONSE,
    ],
};

const UnitedKingdomInfo = () => <CountryInfo info={COUNTRY_INFO} />;

export default UnitedKingdomInfo;
